import type { ComponentPropsWithoutRef } from 'react';
import { SquareArrowOutUpRight } from 'lucide-react';
import { isExternalUrl } from '@/utils/links';

/**
 * Anchor renderer shared by both markdown surfaces (the assistant-ui chat
 * thread and `MarkdownContent`). External links open in a new tab with
 * `rel="noopener noreferrer"` and get a trailing external-link icon so it's
 * obvious the link leaves the app; same-origin and relative links open in
 * place (SPA routing) with no icon.
 *
 * See `isExternalUrl` for the exact same-origin rules.
 */
export function MarkdownLink({ href, children, ...props }: ComponentPropsWithoutRef<'a'>) {
  const external = isExternalUrl(href);
  return (
    <a
      href={href}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      {...props}
    >
      {children}
      {external && (
        // inline-block keeps the link underline from running under the glyph;
        // currentColor makes the icon track the link color.
        <SquareArrowOutUpRight
          aria-hidden="true"
          className="ml-0.5 inline-block h-3 w-3 align-[-0.1em]"
        />
      )}
    </a>
  );
}
